/**
 * Packstation – tastatur- und scanner-first (Prozessdesign 04 §4–5, Entscheidungen Inhaber 17.10.2026).
 *
 * Ein Eingabefeld hat IMMER den Fokus: Ein USB-Scanner tippt wie eine Tastatur
 * und schickt Enter. Was eine Eingabe bedeutet, ergibt sich aus dem Schritt:
 *   Auftrag    – Enter = ältester bereiter Auftrag; Scan = Auftrag/Laufzettel öffnen
 *   Positionen – KEIN Produkt-Scan nötig (R36): Enter oder Antippen bestätigt die markierte
 *                Position, bei Menge > 1 die Zahl eintippen + Enter (Mengenbestätigung).
 *                Scan des Fach-Etiketts (SKU/Platz) oder EAN geht auch; personalisiert: Laufzettel.
 *   Verpackung – Enter = Vorschlag übernehmen, 1–6 + Enter = Wahl
 *   Gewicht    – Feld ist leer, der Rechenwert steht als Platzhalter: Enter übernimmt ihn, gewogene Gramm
 *                eintippen ersetzt ihn (B8). Produkt und Statuszeile folgen sofort dem getippten Wert
 *                (über 1.000 g → DHL Paket, gleiche Regel wie die API: contracts `versandproduktFuer`).
 *   Label      – Enter = Label (A5) beim primären Carrier, Druck automatisch über PrintNode (R40);
 *                technischer Carrier-Fehler → F6 = Fallback-Carrier (R34)
 *   Bestätigen – Label-Barcode scannen → nächster Auftrag automatisch
 * Tasten: F2 Label erneut drucken · F4 Klärfall · F6 Fallback-Carrier · Esc zurücklegen.
 *
 * Re-Abnahme 1:
 *   T6  Storno ist in jedem Schritt ein Alarmfeld (Vollbreite, Ton, Fokus aufs Scanfeld, Enter bestätigt):
 *       Abfrage des Zustands im Takt (ohne Aktion) und Neuladen bei jeder abgelehnten Aktion (409 storno).
 *   T8  Eingaben vor der Bereitschaft puffert /scanpuffer.js; die Station übernimmt sie beim Start in Reihenfolge.
 *   K4  Format-Erkennung: Bestellnummer bzw. Laufzettel eines anderen Auftrags ist immer Auftragswahl, nie Position.
 * Jede Aktion ist auch als großer Knopf da (Tablet ohne F-Tasten); Knöpfe
 * nehmen dem Scanfeld den Fokus nicht.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { KLAERFALL_GRUENDE, KLEINPAKET_MAX_G, parseLaufzettel, versandproduktFuer, versandproduktText, type KlaerfallGrund } from '@storeos/contracts';
import { ApiError } from '../api';
import * as ps from '../api/packstation';
import { labelPdfPfad, labelZplPfad } from '../api/belege';
import type { PackPosition, PackZustand } from '../api/typen';
import { DateiLink, oeffneDatei } from '../components/DateiLink';
import { datumZeit, gewicht as fmtGewicht, KLAERFALL_TEXT, ohneRohwerte, SENDUNG_TEXT } from '../components/format';
import { SendungBadge, RouteBadge } from '../components/Status';
import { ton } from '../components/ton';
import { Dialog, Feld, useToast } from '../components/ui';

/** R104: Text, wenn ein Artikel der Sendung kein bekanntes Gewicht hat. */
const GEWICHT_UNBEKANNT = 'Gewicht unbekannt – bitte wiegen';

type Schritt = 'auftrag' | 'scannen' | 'verpackung' | 'gewicht' | 'label' | 'bestaetigen';
const SCHRITTE: { s: Schritt; text: string }[] = [
  { s: 'auftrag', text: 'Auftrag' }, { s: 'scannen', text: 'Positionen' }, { s: 'verpackung', text: 'Verpackung' },
  { s: 'gewicht', text: 'Gewicht' }, { s: 'label', text: 'Label' }, { s: 'bestaetigen', text: 'Label-Scan' },
];
const AUFFORDERUNG: Record<Schritt, string> = {
  auftrag: 'Enter = nächster Auftrag · oder Auftragsnummer / Kiste scannen',
  scannen: 'Enter = markierte Position bestätigen · Menge eintippen + Enter · Fach-Etikett scannen · personalisiert: Laufzettel',
  verpackung: 'Enter = Vorschlag übernehmen · 1–6 = andere Verpackung',
  gewicht: 'Enter = Rechenwert übernehmen · gewogenes Gewicht in Gramm eintippen + Enter',
  label: 'Enter = Label erzeugen und drucken',
  bestaetigen: 'Label aufkleben, dann Label-Barcode scannen',
};
const CARRIER_NAME: Record<string, string> = { dhl: 'DHL', shipcloud: 'Shipcloud', mock: 'Mock' };
const carrierName = (c: string | null | undefined) => (c ? CARRIER_NAME[c] ?? c : '–');
const FORMAT_NAME: Record<string, string> = { pdf_a5: 'A5', pdf_a6_100x150: 'A6 100×150', pdf_a4: 'A4', zpl_100x150: 'ZPL' };

/** Erste offene Position, die man ohne Laufzettel bestätigen darf (sonst die erste offene). */
function ersteOffene(p: PackZustand | null): PackPosition | null {
  const offen = p?.positionen.filter((x) => x.gepackt_menge < x.menge) ?? [];
  return offen.find((x) => !x.laufzettel_pflicht) ?? offen[0] ?? null;
}

/** Schritt der Oberfläche aus dem Schritt der API (PackZustand.schritt). */
function schrittVon(p: PackZustand | null): Schritt {
  if (!p) return 'auftrag';
  switch (p.schritt) {
    case 'verpackung': return 'verpackung';
    case 'gewicht': return 'gewicht';
    case 'label': return 'label';
    case 'label_scan': case 'fertig': return 'bestaetigen';
    default: return 'scannen';
  }
}

/** Fehlercodes der API, nach denen der Stand neu geladen wird (Storno oder Status von anderer Stelle geändert). */
const NEU_LADEN = new Set(['storno', 'kein_label', 'nicht_in_pick', 'nicht_gepackt', 'label_verworfen', 'verpackung_fehlt', 'gewicht_fehlt', 'label_laeuft']);
/** Takt der Storno-Abfrage, solange ein Auftrag offen ist. */
const ABFRAGE_MS = 2500;

/**
 * K4: Ist der Scan eine Auftragswahl? Laufzettel eines anderen Auftrags oder eine Bestellnummer (optional „-n“),
 * die zu keiner Position (SKU, EAN, Fach) passt. 'eigener' = Nummer des offenen Auftrags.
 */
export function auftragsScan(code: string, p: PackZustand | null): 'anderer' | 'eigener' | null {
  if (!p) return null;
  const lz = parseLaufzettel(code);
  if (lz) return lz.bestellnummer.toUpperCase() === p.auftrag.bestellnummer.toUpperCase() ? null : 'anderer';
  const m = /^(\d{4,7})(?:-(\d{1,2}))?$/.exec(code.trim());
  if (!m) return null;
  const c = code.trim().toLowerCase();
  if (p.positionen.some((x) => [x.sku, x.ean, x.bin_code].some((v) => v && v.toLowerCase() === c))) return null;
  return m[1] === p.auftrag.bestellnummer && (!m[2] || Number(m[2]) === p.sendung.nummer) ? 'eigener' : 'anderer';
}

declare global {
  interface Window { __scanPuffer?: string[]; __scanZiel?: (taste: string) => void }
}

const FARBE: Record<string, string> = { 'Leinen Grün': '#5f7f5a', 'Leinen Natur': '#d8cdb5', 'Leinen Schwarz': '#2b2b2b', 'Leinen Bordeaux': '#7a2836' };

function lesePraeferenz(k: string, standard: boolean) {
  try { const v = localStorage.getItem(k); return v === null ? standard : v === '1'; } catch { return standard; }
}
function schreibePraeferenz(k: string, v: boolean) { try { localStorage.setItem(k, v ? '1' : '0'); } catch { /* egal */ } }

export function Packstation() {
  const toast = useToast();
  const [pack, setPackState] = useState<PackZustand | null>(null);
  // Refs spiegeln den Stand für die Scan-Warteschlange (schnelle Scanner warten nicht auf React-Renders).
  const packRef = useRef<PackZustand | null>(null);
  const setPack = (p: PackZustand | null) => { packRef.current = p; setPackState(p); };
  const wahlRef = useRef<string | null>(null);
  const labelAtRef = useRef<number | null>(null);
  const kette = useRef<Promise<void>>(Promise.resolve());
  const [eingabe, setEingabeState] = useState('');
  const eingabeRef = useRef('');
  const setEingabe = (v: string) => { eingabeRef.current = v; setEingabeState(v); };
  const [meldung, setMeldung] = useState<{ text: string; art: 'ok' | 'fehler' | 'info' } | null>(null);
  const [blitz, setBlitz] = useState(false);
  const [busy, setBusy] = useState(false);
  const [leer, setLeer] = useState(false);
  const [wahlId, setWahlState] = useState<string | null>(null);
  const setWahlId = (id: string | null) => { wahlRef.current = id; setWahlState(id); };
  const [dialog, setDialog] = useState<null | 'klaerfall'>(null);
  const [dauern, setDauern] = useState<number[]>([]);
  // Gedruckt wird über PrintNode (R40); das PDF im Browser ist nur noch Rückfall.
  const [autoOeffnen, setAutoOeffnen] = useState(() => lesePraeferenz('storeos-pack-auto-label-browser', false));
  const [aktuellId, setAktuellState] = useState<string | null>(null);
  const aktuellRef = useRef<string | null>(null);
  const setAktuell = (id: string | null) => { aktuellRef.current = id; setAktuellState(id); };
  const [fallback, setFallback] = useState<string | null>(null);
  const [vollbild, setVollbild] = useState(() => lesePraeferenz('storeos-pack-vollbild', false));
  const feld = useRef<HTMLInputElement>(null);
  const schritt = schrittVon(pack);
  const stornoKlaerfall = Boolean(pack?.storno_alarm && pack.sendung.status === 'klaerfall');
  const paket = pack?.paket ?? null;

  useEffect(() => {
    document.body.classList.toggle('vollbild', vollbild);
    return () => document.body.classList.remove('vollbild');
  }, [vollbild]);

  const fokus = useCallback(() => { if (!dialog) setTimeout(() => feld.current?.focus(), 0); }, [dialog]);
  useEffect(() => { fokus(); }, [fokus, pack, dialog]);

  const vorschlagId = pack?.verpackung_vorschlag?.id ?? null;
  useEffect(() => { setWahlId(pack?.sendung.packaging_id ?? vorschlagId); }, [pack?.sendung.id, vorschlagId, pack?.sendung.packaging_id]);
  // Markierte Position: bleibt, solange sie offen ist, sonst die nächste offene.
  useEffect(() => {
    const offen = pack?.positionen.find((x) => x.order_item_id === aktuellRef.current && x.gepackt_menge < x.menge);
    if (!offen) setAktuell(ersteOffene(pack)?.order_item_id ?? null);
  }, [pack]);
  // B8: Gewicht – das Feld bleibt leer (Rechenwert = Platzhalter). Getippte Ziffern ersetzen ihn, auch wenn sie
  // schon während des Verpackungs-Requests kamen (Eingabe wird hier nie überschrieben). Enter ohne Eingabe übernimmt ihn.
  useEffect(() => {
    if (schritt !== 'label') setFallback(null);
  }, [schritt, pack?.sendung.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live-Anzeige im Schritt Gewicht: getippter Wert → Gewicht, Produkt und Statuszeile sofort danach.
  const getippt = schritt === 'gewicht' ? gramm(eingabe) : null;
  const anzeigeGewicht = getippt ?? (pack?.sendung.gewicht_bestaetigt_at ? pack.sendung.gewicht_g : pack?.gewicht_vorschlag_g) ?? null;
  const anzeigeProdukt = schritt === 'gewicht' && pack && anzeigeGewicht
    ? versandproduktFuer(pack.auftrag.lieferland, anzeigeGewicht, pack.verpackung?.nr ?? null, pack.kleinpaket_erlaubt ?? true)
    : pack?.sendung.versandprodukt ?? null;
  const liveMeldung = getippt !== null && pack
    ? `Gewicht ${fmtGewicht(getippt)} eingetippt → ${versandproduktText(anzeigeProdukt)}${getippt > KLEINPAKET_MAX_G && pack.auftrag.lieferland === 'DE' ? ' (über 1 kg, kein Kleinpaket)' : ''}. Enter = übernehmen.`
    : null;

  const fehler = useCallback((text: string) => {
    setMeldung({ text: ohneRohwerte(text), art: 'fehler' });
    setBlitz(true); setTimeout(() => setBlitz(false), 700);
    ton('fehler');
  }, []);
  const ok = (text: string, laut: 'ok' | 'fertig' = 'ok') => { setMeldung({ text, art: 'ok' }); ton(laut); };

  /** T6: Storno erkannt – Alarmfeld, Ton (dreimal), Fokus aufs Scanfeld (Enter bestätigt die Rücklage). */
  const stornoAlarm = useCallback((p: PackZustand) => {
    setMeldung({ text: `STORNO: Auftrag ${p.auftrag.kennung} wurde im Shop storniert. Nicht versenden.`, art: 'fehler' });
    setBlitz(true); setTimeout(() => setBlitz(false), 700);
    ton('fehler'); setTimeout(() => ton('fehler'), 450); setTimeout(() => ton('fehler'), 900);
    feld.current?.focus();
  }, []);

  /** Stand der offenen Sendung neu laden (nach abgelehnter Aktion); bei Storno sofort das Alarmfeld. */
  async function neuLaden(sid: string): Promise<PackZustand | null> {
    const neu = await ps.zustand(sid).catch(() => null);
    if (!neu || packRef.current?.sendung.id !== sid) return null;
    const warAlarm = Boolean(packRef.current?.storno_alarm);
    setPack(neu);
    if (neu.storno_alarm && !warAlarm) stornoAlarm(neu);
    return neu;
  }

  async function lauf<T>(f: () => Promise<T>): Promise<T | undefined> {
    setBusy(true);
    try { return await f(); } catch (e) {
      fehler((e as Error).message);
      const code = ((e as ApiError).details as { code?: string } | undefined)?.code;
      const offen = packRef.current;
      if (offen && code && NEU_LADEN.has(code)) await neuLaden(offen.sendung.id);
      return undefined;
    } finally { setBusy(false); }
  }

  async function oeffne(code?: string) {
    const p = await lauf(() => ps.naechste(code));
    if (p === undefined) return;
    if (!p) { setPack(null); setLeer(true); setMeldung({ text: 'Keine bereiten Aufträge. Gut gemacht.', art: 'info' }); return; }
    setLeer(false);
    setPack(p);
    if (labelAtRef.current) { const s = Math.round((Date.now() - labelAtRef.current) / 1000); setDauern((d) => [...d.slice(-49), s]); labelAtRef.current = null; }
    if (p.storno_alarm) stornoAlarm(p);
    else ok(`Auftrag ${p.auftrag.bestellnummer} geöffnet · ${p.positionen.reduce((s, x) => s + x.menge, 0)} Einheiten`);
  }

  async function verpackungUebernehmen(id: string | null) {
    const pack = packRef.current;
    if (!pack || !id) return;
    const nr = pack.verpackungen.find((v) => v.id === id)?.nr;
    const p = await lauf(() => ps.verpackung(pack.sendung.id, nr));
    if (p) { setPack(p); ok(`Verpackung ${p.verpackung?.nr ?? nr ?? ''} · Rechenwert ${fmtGewicht(p.gewicht_vorschlag_g)} · ${versandproduktText(p.sendung.versandprodukt)}. Gewicht: Enter übernimmt, gewogene Gramm eintippen.`); }
  }

  async function labelDrucken(mitFallback = false) {
    const pack = packRef.current;
    if (!pack) return;
    if (pack.storno_alarm) { stornoAlarm(pack); return; }
    setBusy(true);
    let p: PackZustand;
    try {
      p = await ps.label(pack.sendung.id, mitFallback);
    } catch (e) {
      fehler((e as Error).message);
      // Technischer Fehler beim primären Carrier (5xx/Timeout): Fallback per Taste anbieten (R34), nie bei Adressfehlern.
      const d = (e as ApiError).details as { code?: string; details?: { fallback?: string | null } } | undefined;
      setFallback(!mitFallback && d?.code === 'carrier' && d.details?.fallback ? d.details.fallback : null);
      // z. B. Adresse vom Carrier abgelehnt: Sendung ist jetzt Klärfall. Stand neu laden, Meldung bleibt stehen.
      await neuLaden(pack.sendung.id);
      return;
    } finally { setBusy(false); }
    setFallback(null);
    setPack(p);
    labelAtRef.current = Date.now();
    const pk = p.paket;
    const druck = p.druck?.status === 'erledigt' ? `an Drucker gesendet (PrintNode ${p.druck.modus === 'live' ? 'LIVE' : 'MOCK'})`
      : p.druck?.status === 'aus' ? 'Druck aus: PDF öffnen' : `Druck ${p.druck?.status ?? '–'}${p.druck?.fehler ? `: ${p.druck.fehler}` : ''}`;
    ok(`Label ${carrierName(p.sendung.carrier)} ${FORMAT_NAME[pk?.label_format ?? ''] ?? ''} · ${pk?.tracking_nummer ?? ''} · ${druck}. Aufkleben und scannen.`, 'fertig');
    if (pk && (autoOeffnen || p.druck?.status === 'fehlgeschlagen' || p.druck?.status === 'aus')) void oeffneDatei(labelPdfPfad(pk.id));
  }

  async function bestaetige(pos: PackPosition, menge: number, per: 'tippen' | 'enter') {
    const pack = packRef.current;
    if (!pack) return;
    const r = await lauf(() => ps.bestaetigen(pack.sendung.id, menge, pos.order_item_id, per));
    if (!r) return;
    setPack(r);
    const fertig = r.ergebnis === 'abgeschlossen';
    ok(fertig ? `${r.gescannt} ✓ · alle Positionen bestätigt. Enter = Verpackung übernehmen.` : `${r.gescannt} ✓ ${menge}× (${r.fortschritt.gepackt}/${r.fortschritt.gesamt})`, fertig ? 'fertig' : 'ok');
  }

  /** Antippen einer Position: Menge 1 sofort, sonst markieren und Menge abfragen. */
  function tippe(pos: PackPosition) {
    if (schrittVon(packRef.current) !== 'scannen' || pos.gepackt_menge >= pos.menge) return;
    if (pos.laufzettel_pflicht) { fehler('Personalisiert: Laufzettel-Barcode scannen (Datenschutz).'); return; }
    setAktuell(pos.order_item_id);
    const rest = pos.menge - pos.gepackt_menge;
    if (rest === 1) kette.current = kette.current.then(() => bestaetige(pos, 1, 'tippen')).catch(() => {});
    else { setMeldung({ text: `${pos.name}: Menge ${rest} bestätigen – ${rest} eintippen + Enter`, art: 'info' }); fokus(); }
  }

  function absenden(e: FormEvent) {
    e.preventDefault();
    const code = eingabeRef.current.trim();
    setEingabe('');
    einreihen(code);
  }

  // Eingaben werden der Reihe nach abgearbeitet, auch wenn der Scanner schneller ist als die API.
  // Über die Ref, damit auch Tasten aus dem Puffer (T8) die aktuelle Logik nutzen.
  const verarbeiteRef = useRef<(code: string) => Promise<void>>(async () => {});
  function einreihen(code: string) {
    kette.current = kette.current.then(() => verarbeiteRef.current(code)).catch(() => {});
  }

  // T8: Tasten vor der Bereitschaft (Puffer aus /scanpuffer.js) in Reihenfolge übernehmen; danach leitet der Puffer
  // Tasten außerhalb des Scanfelds direkt hierher (z. B. während ein Knopf kurz den Fokus hatte).
  useEffect(() => {
    const taste = (k: string) => {
      if (k === 'Enter') { const code = eingabeRef.current.trim(); setEingabe(''); einreihen(code); }
      else setEingabe(eingabeRef.current + k);
      feld.current?.focus();
    };
    for (const k of window.__scanPuffer?.splice(0) ?? []) taste(k);
    window.__scanZiel = taste;
    feld.current?.focus();
    return () => { if (window.__scanZiel === taste) window.__scanZiel = undefined; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // T6: Storno-Abfrage im Takt, solange ein Auftrag offen ist und noch kein Alarm steht (auch ohne jede Aktion).
  const offenId = pack && !pack.storno_alarm ? pack.sendung.id : null;
  useEffect(() => {
    if (!offenId) return;
    const t = setInterval(() => {
      void ps.zustand(offenId).then((z) => {
        const akt = packRef.current;
        if (!akt || akt.sendung.id !== offenId || akt.storno_alarm || !z.storno_alarm) return;
        setPack(z);
        stornoAlarm(z);
      }).catch(() => undefined);
    }, ABFRAGE_MS);
    return () => clearInterval(t);
  }, [offenId, stornoAlarm]); // eslint-disable-line react-hooks/exhaustive-deps

  /** K4: anderen Auftrag wählen. Unberührter Auftrag (nichts bestätigt) geht zurück auf bereit, sonst Hinweis. */
  async function wechsleAuftrag(code: string, pack: PackZustand) {
    if (pack.sendung.status === 'in_pick' && pack.fortschritt.gepackt === 0) {
      const r = await lauf(() => ps.zuruecklegen(pack.sendung.id));
      if (r === undefined) return;
      setPack(null);
      await oeffne(code);
      return;
    }
    fehler(`Auftrag ${pack.auftrag.kennung} ist begonnen: erst abschließen oder Esc (zurücklegen), dann ${code} scannen.`);
  }

  async function verarbeite(code: string) {
    const pack = packRef.current;
    const wahlId = wahlRef.current;
    // B5: Storno-Alarm – Enter bestätigt, dass die Ware (bzw. das gelabelte Paket vom Abholplatz) zurück im Fach ist.
    if (pack?.storno_alarm && pack.sendung.status === 'klaerfall') {
      if (code) { fehler('STORNO: erst die Ware zurücklegen, dann Enter.'); return; }
      await stornoErledigt();
      return;
    }
    // K4: Bestellnummer bzw. Laufzettel eines anderen Auftrags = Auftragswahl, nie Positions-Scan.
    // Nicht im Schritt Gewicht (Gramm sind Ziffern) und nicht am Label-Scan (dort zählt nur das Label).
    const wahl = code && !['gewicht', 'bestaetigen'].includes(schrittVon(pack)) ? auftragsScan(code, pack) : null;
    if (pack && wahl === 'eigener') { setMeldung({ text: `Auftrag ${pack.auftrag.kennung} ist bereits offen.`, art: 'info' }); return; }
    if (pack && wahl === 'anderer') { await wechsleAuftrag(code, pack); return; }
    switch (schrittVon(pack)) {
      case 'auftrag': await oeffne(code || undefined); break;
      case 'scannen': {
        if (!pack) return;
        const pos = pack.positionen.find((x) => x.order_item_id === aktuellRef.current && x.gepackt_menge < x.menge) ?? ersteOffene(pack);
        // Enter oder Zahl + Enter: markierte Position bestätigen (Mengenbestätigung bei Menge > 1).
        if (!code || /^\d{1,3}$/.test(code)) {
          if (!pos) return;
          if (pos.laufzettel_pflicht) { fehler(`${pos.name}: personalisiert – Laufzettel-Barcode scannen (Datenschutz).`); return; }
          const rest = pos.menge - pos.gepackt_menge;
          const menge = code ? Number(code) : rest === 1 ? 1 : 0;
          if (!menge) { setMeldung({ text: `${pos.name}: Menge ${rest} bestätigen – ${rest} eintippen + Enter`, art: 'info' }); return; }
          await bestaetige(pos, menge, code ? 'tippen' : 'enter');
          return;
        }
        // Scan: Laufzettel, Fach-Etikett (SKU/Platz) oder EAN; „3*CODE“ bucht 3 Stück.
        const m = /^(\d{1,3})[*x](.+)$/i.exec(code);
        const r = await lauf(() => ps.scan(pack.sendung.id, m ? m[2] : code, m ? Number(m[1]) : undefined));
        if (!r) return;
        setPack(r);
        const fertig = r.ergebnis === 'abgeschlossen';
        const stand = `${r.fortschritt.gepackt}/${r.fortschritt.gesamt}`;
        ok(fertig ? `${r.gescannt} ✓ · alle Positionen bestätigt. Enter = Verpackung übernehmen.` : `${r.gescannt} ✓ (${stand})`, fertig ? 'fertig' : 'ok');
        break;
      }
      case 'verpackung': {
        if (/^[1-6]$/.test(code)) { const v = pack?.verpackungen.find((x) => x.nr === Number(code)); if (v) await verpackungUebernehmen(v.id); return; }
        if (code) { fehler('Verpackung: Enter oder 1–6'); return; }
        await verpackungUebernehmen(wahlId);
        break;
      }
      case 'gewicht': {
        if (!pack) return;
        const wert = gramm(code);
        if (code && wert === null) { fehler('Gewicht in Gramm eintippen, z. B. 850'); setEingabe(code); return; }
        // R104: Artikelgewicht unbekannt → der Rechenwert ist nur ein Teilwert, Wiegen ist Pflicht.
        if (pack.gewicht_unbekannt && !wert) { fehler(GEWICHT_UNBEKANNT); return; }
        const p = await lauf(() => ps.gewicht(pack.sendung.id, wert && (pack.gewicht_unbekannt || wert !== pack.gewicht_vorschlag_g) ? wert : undefined));
        if (p) { setPack(p); ok(`Gewicht ${fmtGewicht(p.sendung.gewicht_g)} (${p.sendung.gewicht_quelle === 'eingetragen' ? 'eingetragen' : 'Rechenwert übernommen'}) · ${versandproduktText(p.sendung.versandprodukt)}. Enter = Label.`); }
        break;
      }
      case 'label': if (code) { fehler('Enter erzeugt das Label. Kein Scan nötig.'); return; } await labelDrucken(); break;
      case 'bestaetigen': {
        if (!pack) return;
        if (!code) { setMeldung({ text: 'Label-Barcode scannen. F2 = Label neu drucken.', art: 'info' }); return; }
        const r = await lauf(() => ps.labelBestaetigen(pack.sendung.id, code));
        if (!r) return;
        toast(`Sendung ${pack.auftrag.bestellnummer}-${pack.sendung.nummer} fertig`);
        await oeffne();
        break;
      }
    }
  }

  verarbeiteRef.current = verarbeite;

  // Ziffern 1–6 heben im Schritt Verpackung sofort die Wahl hervor.
  function aendern(v: string) {
    setEingabe(v);
    if (schritt === 'verpackung' && /^[1-6]$/.test(v.trim())) {
      const vp = pack?.verpackungen.find((x) => x.nr === Number(v.trim()));
      if (vp) setWahlId(vp.id);
    }
  }

  async function stornoErledigt() {
    const p = packRef.current;
    if (!p) return;
    const r = await lauf(() => ps.stornoZurueckgelegt(p.sendung.id));
    if (!r) return;
    toast(`Storno ${p.auftrag.kennung} erledigt: Ware zurück im Fach, Reservierung frei`);
    setPack(null);
    setMeldung({ text: `Storno ${p.auftrag.kennung} erledigt. Enter = nächster Auftrag.`, art: 'info' });
  }

  const zuruecklegen = useCallback(async () => {
    if (!pack) return;
    if (pack.sendung.status === 'label_erstellt') { setPack(null); setMeldung({ text: 'Label bleibt gültig; die Sendung geht mit dem Tagesabschluss raus.', art: 'info' }); return; }
    if (pack.sendung.status !== 'in_pick' && pack.sendung.status !== 'gepackt') { setPack(null); setMeldung({ text: `Auftrag ${pack.auftrag.bestellnummer} geschlossen (${SENDUNG_TEXT[pack.sendung.status]}).`, art: 'info' }); return; }
    const r = await lauf(() => ps.zuruecklegen(pack.sendung.id));
    if (r !== undefined) { setMeldung({ text: `Auftrag ${pack.auftrag.bestellnummer} zurückgelegt (bereit).`, art: 'info' }); setPack(null); }
  }, [pack]); // eslint-disable-line react-hooks/exhaustive-deps

  const labelNeu = useCallback(async () => {
    const pack = packRef.current;
    if (!pack?.paket) { fehler('Noch kein Label vorhanden.'); return; }
    const p = await lauf(() => ps.nachdruck(pack.sendung.id));
    if (!p) { void oeffneDatei(labelPdfPfad(pack.paket.id)); return; }
    setPack(p);
    if (p.druck?.status === 'erledigt') setMeldung({ text: `Label ${pack.paket.tracking_nummer} erneut gedruckt (gleiche Sendungsnummer).`, art: 'info' });
    else { fehler(`Druck ${p.druck?.status ?? ''}: ${p.druck?.fehler ?? ''} – PDF öffnet im Browser.`); void oeffneDatei(labelPdfPfad(pack.paket.id)); }
  }, [fehler]); // eslint-disable-line react-hooks/exhaustive-deps

  // Funktionstasten global (auch wenn der Fokus kurz woanders ist).
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (dialog) return;
      if (e.key === 'F2') { e.preventDefault(); void labelNeu(); }
      else if (e.key === 'F4') { e.preventDefault(); if (pack) setDialog('klaerfall'); else fehler('Kein Auftrag offen.'); }
      else if (e.key === 'F6') { e.preventDefault(); if (fallback && schritt === 'label') kette.current = kette.current.then(() => labelDrucken(true)).catch(() => {}); else fehler('Fallback-Carrier nur nach einem technischen Fehler des primären Carriers.'); }
      else if (e.key === 'Escape') { e.preventDefault(); void zuruecklegen(); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [dialog, pack, schritt, labelNeu, zuruecklegen, fehler, fallback]); // eslint-disable-line react-hooks/exhaustive-deps

  const schnitt = dauern.length ? Math.round(dauern.reduce((a, b) => a + b, 0) / dauern.length) : null;
  const wahl = pack?.verpackungen.find((v) => v.id === wahlId);
  const knopf = (onClick: () => void, taste: string, text: string, aus = false, klasse = '') => (
    <button type="button" className={`taste ${klasse}`} disabled={aus || busy} onMouseDown={(e) => e.preventDefault()} onClick={onClick}>
      <kbd>{taste}</kbd><span>{text}</span>
    </button>
  );

  return (
    <section className={`packstation${blitz ? ' blitz' : ''}${pack?.storno_alarm ? ' alarm' : ''}`} data-schritt={schritt}>
      <div className="ps-kopf">
        <ol className="ps-schritte" aria-label="Schritte">
          {SCHRITTE.map((x, i) => (
            <li key={x.s} className={x.s === schritt ? 'aktiv' : SCHRITTE.findIndex((y) => y.s === schritt) > i ? 'erledigt' : ''}>
              <span className="nr">{i + 1}</span>{x.text}
            </li>
          ))}
        </ol>
        <div className="ps-kennzahl" title="Zeit vom Label bis zum nächsten geöffneten Auftrag (Ziel unter 30 s)" data-testid="kennzahl">
          <span>Label → nächster</span>
          <strong className={dauern.length && dauern[dauern.length - 1] > 30 ? 'warn' : ''}>{dauern.length ? `${dauern[dauern.length - 1]} s` : '–'}</strong>
          <span>Ø {schnitt !== null ? `${schnitt} s` : '–'} · {dauern.length} Pakete</span>
        </div>
      </div>

      <form className="ps-scan" onSubmit={absenden}>
        <label htmlFor="scanfeld" className="ps-scan-label">{schritt === 'gewicht' ? 'Gewicht g' : 'Scan'}</label>
        <input id="scanfeld" ref={feld} data-testid="scanfeld" value={eingabe} onChange={(e) => aendern(e.target.value)}
          onBlur={fokus} autoComplete="off" autoCorrect="off" spellCheck={false} inputMode={schritt === 'gewicht' ? 'numeric' : 'none'} aria-describedby="ps-aufforderung"
          placeholder={stornoKlaerfall ? 'STORNO: Ware zurücklegen, dann Enter' : schritt === 'gewicht' && pack ? (pack.gewicht_unbekannt ? `${GEWICHT_UNBEKANNT}: Gramm eintippen + Enter` : `${pack.gewicht_vorschlag_g} g Rechenwert – Enter übernimmt, gewogene Gramm eintippen`) : AUFFORDERUNG[schritt]} />
        {schritt === 'gewicht' && (pack?.gewicht_unbekannt
          ? <span className="ps-einheit warn-text" data-testid="gewicht-unbekannt">g · {GEWICHT_UNBEKANNT} (Rechenwert ohne Artikel ohne Gewicht: {pack.gewicht_vorschlag_g} g)</span>
          : <span className="ps-einheit" data-testid="gewicht-einheit">g · Rechenwert {pack?.gewicht_vorschlag_g ?? '–'} g</span>)}
        <span id="ps-aufforderung" className="ps-bereit">{busy ? 'arbeitet …' : 'bereit'}</span>
      </form>

      <div className={`ps-meldung ${liveMeldung ? 'info' : meldung?.art ?? 'info'}`} role="status" aria-live="assertive" data-testid="meldung">
        {liveMeldung ?? meldung?.text ?? AUFFORDERUNG[schritt]}
      </div>

      {pack?.storno_alarm && (
        <StornoAlarm pack={pack} onErledigt={() => { kette.current = kette.current.then(() => stornoErledigt()).catch(() => {}); }} />
      )}
      {fallback && schritt === 'label' && (
        <div className="ps-fallback" role="alert" data-testid="fallback">
          <strong>{carrierName(pack?.carrier.primaer)} technisch nicht erreichbar.</strong> F6 erzeugt das Label über {carrierName(fallback)}; Enter versucht es erneut bei {carrierName(pack?.carrier.primaer)}.
          <button type="button" className="knopf" onMouseDown={(e) => e.preventDefault()} onClick={() => { kette.current = kette.current.then(() => labelDrucken(true)).catch(() => {}); }}>F6 · Label über {carrierName(fallback)}</button>
        </div>
      )}

      {!pack ? (
        <div className="ps-leer">
          <p className="gross">{leer ? 'Keine bereiten Aufträge.' : 'Enter drücken für den nächsten Auftrag.'}</p>
          <p className="hinweis">Reihenfolge: ältester bezahlter Auftrag zuerst. Eine Auftragsnummer oder Kisten-Barcode scannen öffnet genau diesen.</p>
          <button type="button" className="knopf gross" onMouseDown={(e) => e.preventDefault()} onClick={() => oeffne()} disabled={busy}>Nächster Auftrag</button>
        </div>
      ) : (
        <div className="ps-arbeit">
          <div className="ps-positionen" data-testid="positionen">
            {pack.positionen.map((p) => <Position key={p.shipment_item_id} p={p} aktuell={schritt === 'scannen' && p.order_item_id === aktuellId} onTippen={schritt === 'scannen' ? () => tippe(p) : undefined} />)}
          </div>
          <aside className="ps-seite">
            <div className="ps-block">
              <div className="ps-auftragnr" data-testid="auftragsnummer">{pack.auftrag.bestellnummer}-{pack.sendung.nummer}</div>
              <div className="zeile"><SendungBadge status={pack.sendung.status} /> <span className="hinweis">bezahlt {datumZeit(pack.auftrag.bezahlt_at)}</span></div>
              {pack.lieferadresse && (
                <address className="ps-adresse">
                  {pack.lieferadresse.vorname} {pack.lieferadresse.nachname}<br />
                  {pack.lieferadresse.strasse} {pack.lieferadresse.hausnummer}<br />
                  {pack.lieferadresse.plz} {pack.lieferadresse.ort} · {pack.lieferadresse.land}
                </address>
              )}
              {pack.auftrag.kundenhinweis && <p className="ps-hinweis">Kundenhinweis: {pack.auftrag.kundenhinweis}</p>}
              {pack.sendung.status === 'klaerfall' && !pack.storno_alarm && <p className="ps-hinweis warn-text">Klärfall: {pack.sendung.klaerfall_text ?? (pack.sendung.klaerfall_grund ? KLAERFALL_TEXT[pack.sendung.klaerfall_grund] : '')}</p>}
            </div>

            {/* N16: Beilagen gleich unter der Adresse, damit sie auch bei 1024×768 ohne Scrollen sichtbar sind. */}
            {pack.beilagen.length > 0 && (
              <div className="ps-block ps-beilagen" data-testid="beilagen">
                <h3>Beilagen</h3>
                <ul>{pack.beilagen.map((b) => <li key={b}>{b}</li>)}</ul>
              </div>
            )}

            {paket && (
              <div className="ps-block ps-label">
                <h3>Label <span className="badge neutral" data-testid="label-carrier">{carrierName(pack.sendung.carrier)}{paket.ist_mock ? ' · MOCK' : ''}</span> <span className="badge neutral" data-testid="label-format">{FORMAT_NAME[paket.label_format] ?? paket.label_format}</span></h3>
                <div className="ps-tracking" data-testid="tracking">{paket.tracking_nummer}</div>
                {pack.druck && (
                  <p className={`ps-druck ${pack.druck.status}`} data-testid="druck-status">
                    {pack.druck.status === 'erledigt' ? `Gedruckt über PrintNode ${pack.druck.modus === 'live' ? 'LIVE' : 'MOCK'}` : pack.druck.modus === 'fehler' || pack.druck.modus === 'aus' ? 'PrintNode gesperrt (Einstellungen): PDF im Browser drucken' : pack.druck.status === 'aus' ? 'Automatischer Druck aus' : `Druck ${pack.druck.status}`}
                    {pack.druck.fehler ? ` · ${pack.druck.fehler}` : ''}
                    {pack.druck.datei ? <><br /><small className="mono">{pack.druck.datei}</small></> : null}
                  </p>
                )}
                <div className="knopfreihe links">
                  <DateiLink pfad={labelPdfPfad(paket.id)} className="knopf-zweit" testid="label-pdf">PDF im Browser (Rückfall)</DateiLink>
                  {paket.hat_label_zpl && <DateiLink pfad={labelZplPfad(paket.id)} className="knopf-zweit" download={`${paket.tracking_nummer}.zpl`}>ZPL laden</DateiLink>}
                </div>
                {paket.ist_mock && <p className="hinweis">Mock-Label, nicht versenden.</p>}
              </div>
            )}
            <div className="ps-block">
              <h3>Verpackung</h3>
              {(schritt === 'scannen' || schritt === 'verpackung') && !stornoKlaerfall ? <div className="ps-verpackungen" role="radiogroup" aria-label="Verpackung">
                {pack.verpackungen.filter((v) => v.aktiv).map((v) => (
                  <button key={v.id} type="button" role="radio" aria-checked={v.id === wahlId} disabled={schritt !== 'verpackung' || busy}
                    className={`ps-verp${v.id === wahlId ? ' gewaehlt' : ''}${v.id === vorschlagId ? ' vorschlag' : ''}`}
                    onMouseDown={(e) => e.preventDefault()} onClick={() => setWahlId(v.id)}>
                    <span className="nr">{v.nr}</span><span className="name">{v.name}</span>
                  </button>
                ))}
              </div> : <p className="ps-verp-kurz">{wahl ? `${wahl.nr} · ${wahl.name}` : '–'}</p>}
              <dl className="ps-werte">
                <dt>Vorschlag</dt><dd>{pack.verpackung_vorschlag?.nr ?? '–'}{wahl && wahl.id !== vorschlagId ? ` (gewählt ${wahl.nr})` : ''}</dd>
                <dt>Gewicht</dt><dd data-testid="gewicht">{fmtGewicht(anzeigeGewicht)} <small>{getippt !== null ? 'eingetippt, Enter übernimmt' : pack.sendung.gewicht_bestaetigt_at ? (pack.sendung.gewicht_quelle === 'eingetragen' ? 'eingetragen' : 'Rechenwert übernommen') : 'Rechenwert'}{pack.gewicht_unbekannt ? ` · ${GEWICHT_UNBEKANNT}` : ''}</small></dd>
                <dt>Produkt</dt><dd data-testid="produkt" className={anzeigeProdukt !== pack.sendung.versandprodukt && getippt !== null ? 'ps-produkt-neu' : ''}>{versandproduktText(anzeigeProdukt)}{getippt !== null && anzeigeProdukt !== pack.sendung.versandprodukt ? <small> statt {versandproduktText(pack.sendung.versandprodukt)}</small> : null}</dd>
                <dt>Carrier</dt><dd data-testid="carrier">{carrierName(pack.carrier.primaer)}{pack.carrier.fallback ? <small> · Fallback {carrierName(pack.carrier.fallback)}</small> : null}</dd>
                <dt>Label</dt><dd>{FORMAT_NAME[pack.label_format] ?? pack.label_format} <small>A5-Haftpapier</small></dd>
              </dl>
            </div>

          </aside>
        </div>
      )}

      <div className="ps-tasten" aria-label="Tasten">
        {knopf(() => feld.current?.form?.requestSubmit(), 'Enter',
          stornoKlaerfall ? 'Ware ist zurück im Fach' : schritt === 'auftrag' ? 'Nächster Auftrag' : schritt === 'scannen' ? 'Position bestätigen' : schritt === 'verpackung' ? 'Verpackung übernehmen'
            : schritt === 'gewicht' ? 'Gewicht übernehmen' : schritt === 'label' ? 'Label drucken' : 'Weiter', false, 'primaer')}
        {knopf(() => void labelNeu(), 'F2', 'Label erneut drucken', !paket)}
        {knopf(() => setDialog('klaerfall'), 'F4', 'Klärfall', !pack)}
        {knopf(() => { kette.current = kette.current.then(() => labelDrucken(true)).catch(() => {}); }, 'F6', `Fallback ${carrierName(pack?.carrier.fallback)}`, !fallback || schritt !== 'label')}
        {knopf(() => void zuruecklegen(), 'Esc', 'Zurücklegen', !pack)}
        <label className="ps-option"><input type="checkbox" checked={autoOeffnen} onChange={(e) => { setAutoOeffnen(e.target.checked); schreibePraeferenz('storeos-pack-auto-label-browser', e.target.checked); fokus(); }} /> Label zusätzlich im Browser öffnen</label>
        <label className="ps-option"><input type="checkbox" checked={vollbild} onChange={(e) => { setVollbild(e.target.checked); schreibePraeferenz('storeos-pack-vollbild', e.target.checked); fokus(); }} /> Vollbild</label>
      </div>

      <KlaerfallDialog offen={dialog === 'klaerfall'} pack={pack} onSchliessen={() => setDialog(null)}
        onFertig={(text) => { setDialog(null); setPack(null); setMeldung({ text, art: 'info' }); }} />
    </section>
  );
}

/** Getipptes Gewicht in Gramm („1250“, „1250 g“) oder null. */
function gramm(text: string): number | null {
  const g = text.trim().replace(/\s|g$/gi, '');
  return /^\d{1,5}$/.test(g) && Number(g) > 0 ? Number(g) : null;
}

/**
 * T6: Alarmfeld bei Storno – Vollbreite, rot, Handlungsschritte. Enter im Scanfeld (Fokus bleibt dort) oder der Knopf
 * bestätigt die Rücklage. Mit Label (gedruckt bzw. beim Carrier storniert) zuerst: Label verwerfen.
 */
function StornoAlarm({ pack, onErledigt }: { pack: PackZustand; onErledigt: () => void }) {
  const mitLabel = /Label/.test(pack.sendung.klaerfall_text ?? '') || Boolean(pack.paket) || pack.sendung.status === 'label_erstellt';
  const klaerfall = pack.sendung.status === 'klaerfall';
  // Label beim Carrier NICHT storniert: der Servertext sagt, was zusätzlich zu tun ist (Carrier-Portal).
  const labelOffen = /nicht storniert/i.test(pack.sendung.klaerfall_text ?? '');
  return (
    <div className="ps-storno-alarm" role="alertdialog" aria-modal="false" aria-labelledby="ps-storno-titel" aria-describedby="ps-storno-text" data-testid="storno-alarm">
      <div id="ps-storno-titel" className="ps-storno-titel">STORNO – nicht versenden</div>
      <p id="ps-storno-text" className="ps-storno-text">Auftrag {pack.auftrag.kennung} wurde im Shop storniert.{labelOffen ? ` ${stornoText(pack.sendung.klaerfall_text)}` : ''}</p>
      <ol className="ps-storno-schritte">
        {mitLabel && <li>Label verwerfen: Paket vom Abholplatz nehmen, Label abziehen und wegwerfen</li>}
        <li>Ware zurück ins Fach</li>
        <li>{klaerfall ? 'Enter bestätigt' : 'Esc legt den Auftrag zurück'}</li>
      </ol>
      {klaerfall && (
        <button type="button" className="knopf-gefahr" onMouseDown={(e) => e.preventDefault()} onClick={onErledigt} data-testid="storno-erledigt">Enter · Ware ist zurück im Fach</button>
      )}
    </div>
  );
}

/** Klärfalltext des Storno-Alarms ohne das Präfix „STORNO:“. */
function stornoText(t: string | null): string {
  return (t ?? '').replace(/^STORNO:\s*/, '') || 'Ware zurück an den Platz.';
}

/** `.invalid` ist per RFC 6761 nie auflösbar (Mock-Kanal); solche Bilder gar nicht erst laden. */
function ladbar(url: string | null): boolean {
  if (!url) return false;
  try { return !new URL(url).hostname.endsWith('.invalid'); } catch { return false; }
}

const PER_TEXT: Record<string, string> = { scan: 'EAN-Scan', fach: 'Fach-Etikett', tippen: 'angetippt', enter: 'Enter', laufzettel: 'Laufzettel', override: 'Override' };

function Position({ p, aktuell, onTippen }: { p: PackPosition; aktuell: boolean; onTippen?: () => void }) {
  const fertig = p.gepackt_menge >= p.menge;
  const farbe = p.variante ? FARBE[p.variante] : undefined;
  const tippbar = Boolean(onTippen) && !fertig && !p.laufzettel_pflicht;
  return (
    <article className={`ps-pos${fertig ? ' fertig' : ''}${aktuell ? ' aktuell' : ''}${tippbar ? ' tippbar' : ''}`} data-testid="position" data-ean={p.ean ?? ''} data-sku={p.sku ?? ''} data-laufzettel={p.laufzettel ?? ''}
      data-bin={p.bin_code ?? ''} data-menge={p.menge} aria-current={aktuell || undefined}
      onMouseDown={(e) => { if (onTippen) e.preventDefault(); }} onClick={onTippen} role={tippbar ? 'button' : undefined}>
      <div className="ps-bild" style={farbe ? { background: farbe } : undefined} aria-hidden>
        {ladbar(p.bild_url) ? <img src={p.bild_url!} alt="" /> : <span>{p.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}</span>}
      </div>
      <div className="ps-pos-text">
        <div className="ps-name">{p.name}</div>
        <div className="ps-variante">{p.variante ?? 'ohne Variante'}</div>
        <div className="ps-codes">
          {p.bin_code && <span className="ps-fach">Fach <strong>{p.bin_code}</strong></span>}
          {p.laufzettel_pflicht ? <span className="warn-text">Laufzettel scannen{p.laufzettel ? ` (${p.laufzettel})` : ''}</span> : <span>SKU {p.sku ?? '–'}</span>}
          {' '}<RouteBadge route={p.route} />
        </div>
        {p.override_grund && <div className="hinweis">Override: {p.override_grund}</div>}
        {fertig && p.bestaetigt_per && <div className="hinweis">bestätigt: {PER_TEXT[p.bestaetigt_per] ?? p.bestaetigt_per}</div>}
        {aktuell && !fertig && !p.laufzettel_pflicht && <div className="ps-tipp">{p.menge - p.gepackt_menge > 1 ? `Menge ${p.menge - p.gepackt_menge}: Zahl eintippen + Enter` : 'Enter oder Antippen bestätigt'}</div>}
      </div>
      <div className="ps-menge" aria-label={`${p.gepackt_menge} von ${p.menge}`}>
        <span className="ist">{p.gepackt_menge}</span><span className="soll">/ {p.menge}</span>
      </div>
    </article>
  );
}

function KlaerfallDialog({ offen, pack, onSchliessen, onFertig }: { offen: boolean; pack: PackZustand | null; onSchliessen: () => void; onFertig: (t: string) => void }) {
  const [grund, setGrund] = useState<KlaerfallGrund | ''>('');
  const [text, setText] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  useEffect(() => { if (offen) { setGrund(''); setText(''); setFehler(null); } }, [offen]);
  async function absenden(e: FormEvent) {
    e.preventDefault();
    if (!pack) return;
    if (!grund) { setFehler('Grund ist Pflicht.'); return; }
    try {
      await ps.packKlaerfall(pack.sendung.id, grund, text);
      onFertig(`Sendung ${pack.auftrag.bestellnummer}-${pack.sendung.nummer} ist Klärfall (${KLAERFALL_TEXT[grund]}). Paket ins Klärfach.`);
    } catch (err) { setFehler((err as Error).message); }
  }
  return (
    <Dialog offen={offen} titel="Klärfall melden" onSchliessen={onSchliessen}>
      <form className="formular" onSubmit={absenden}>
        <Feld label="Grund (Pflicht)">
          <select autoFocus value={grund} onChange={(e) => setGrund(e.target.value as KlaerfallGrund)} data-testid="klaerfall-grund">
            <option value="">– bitte wählen –</option>
            {KLAERFALL_GRUENDE.map((g) => <option key={g} value={g}>{KLAERFALL_TEXT[g]}</option>)}
          </select>
        </Feld>
        <Feld label="Notiz"><input value={text} onChange={(e) => setText(e.target.value)} placeholder="z. B. Cover beschädigt" /></Feld>
        {fehler && <p className="fehler" role="alert">{fehler}</p>}
        <div className="knopfreihe">
          <button type="button" className="knopf-zweit" onClick={onSchliessen}>Abbrechen (Esc)</button>
          <button type="submit" className="knopf-gefahr">Klärfall melden (Enter)</button>
        </div>
      </form>
    </Dialog>
  );
}
